export interface DefaultMilestone {
  id: number;
  name: string;
  defaultOffsetDays: number;
  priority: "High" | "Medium" | "Low";
  duration: number;
  linkedTo: number[];
}

export interface Phase {
  id: number;
  name: string;
  expectedDuration: string;
}

export interface HolidayDef {
  name: string;
  month: number;
  day?: number;
  dayOfWeek?: number;
  occurrence?: number | "last";
}

// FTC Sell-Side M&A Process — based on actual First Turn Capital workflow
export const DEFAULT_MILESTONES: DefaultMilestone[] = [
  // Phase 1: Engagement & Pre-Market Prep
  { id: 1,  name: "Engagement Letter Signed",                    defaultOffsetDays: 0,   priority: "High",   duration: 1,  linkedTo: [2, 3] },
  { id: 2,  name: "Kickoff Meeting (On-site)",                   defaultOffsetDays: 3,   priority: "High",   duration: 1,  linkedTo: [4] },
  { id: 3,  name: "Market Value Analysis",                       defaultOffsetDays: 3,   priority: "Medium", duration: 14, linkedTo: [] },
  { id: 4,  name: "Data Room Setup & Document Collection",       defaultOffsetDays: 5,   priority: "Medium", duration: 14, linkedTo: [5] },
  { id: 5,  name: "Engage Sell-Side QoE Provider",               defaultOffsetDays: 7,   priority: "High",   duration: 3,  linkedTo: [6] },
  { id: 6,  name: "Sell-Side Quality of Earnings (QoE)",         defaultOffsetDays: 10,  priority: "High",   duration: 45, linkedTo: [10, 11] },

  // Phase 2: Marketing Materials (overlaps with QoE)
  { id: 7,  name: "Draft Teaser / Blind Profile",                defaultOffsetDays: 21,  priority: "Medium", duration: 10, linkedTo: [8] },
  { id: 8,  name: "Teaser Approved by Client",                   defaultOffsetDays: 35,  priority: "High",   duration: 3,  linkedTo: [12] },
  { id: 9,  name: "Buyer List Development & Approval",           defaultOffsetDays: 14,  priority: "Medium", duration: 14, linkedTo: [12] },
  { id: 10, name: "Begin CIM Drafting",                          defaultOffsetDays: 35,  priority: "Medium", duration: 30, linkedTo: [11] },
  { id: 11, name: "CIM Finalized & Approved",                    defaultOffsetDays: 65,  priority: "High",   duration: 5,  linkedTo: [12] },

  // Phase 3: Go-to-Market (6-week campaign)
  { id: 12, name: "Go-to-Market Launch",                         defaultOffsetDays: 70,  priority: "High",   duration: 1,  linkedTo: [13] },
  { id: 13, name: "Teaser Distribution & Outreach Campaign",     defaultOffsetDays: 71,  priority: "Medium", duration: 14, linkedTo: [14] },
  { id: 14, name: "NDA Execution & CIM Distribution",            defaultOffsetDays: 78,  priority: "Medium", duration: 21, linkedTo: [15] },
  { id: 15, name: "Buyer Q&A / Follow-up Calls",                 defaultOffsetDays: 85,  priority: "Low",    duration: 21, linkedTo: [16] },
  { id: 16, name: "Indications of Interest (IOIs) Due",          defaultOffsetDays: 112, priority: "High",   duration: 1,  linkedTo: [17] },

  // Phase 4: Management Presentations & LOIs
  { id: 17, name: "IOI Review & Shortlist Selection",            defaultOffsetDays: 114, priority: "High",   duration: 3,  linkedTo: [18, 19] },
  { id: 18, name: "Management Presentation Prep & Rehearsal",    defaultOffsetDays: 117, priority: "Medium", duration: 7,  linkedTo: [19] },
  { id: 19, name: "Management Presentations",                    defaultOffsetDays: 124, priority: "High",   duration: 14, linkedTo: [20] },
  { id: 20, name: "Letters of Intent (LOIs) Due",                defaultOffsetDays: 140, priority: "High",   duration: 1,  linkedTo: [21] },
  { id: 21, name: "LOI Negotiation & Selection",                 defaultOffsetDays: 142, priority: "High",   duration: 7,  linkedTo: [22] },

  // Phase 5: Confirmatory Diligence & Closing
  { id: 22, name: "Exclusivity / LOI Signed",                    defaultOffsetDays: 150, priority: "High",   duration: 1,  linkedTo: [23, 24] },
  { id: 23, name: "Buy-Side Quality of Earnings",                defaultOffsetDays: 151, priority: "High",   duration: 45, linkedTo: [25] },
  { id: 24, name: "Legal Due Diligence / Environmental / Other", defaultOffsetDays: 151, priority: "Medium", duration: 30, linkedTo: [25] },
  { id: 25, name: "Purchase Agreement Drafting & Negotiation",   defaultOffsetDays: 180, priority: "High",   duration: 21, linkedTo: [26] },
  { id: 26, name: "Final SPA Review & Schedules",                defaultOffsetDays: 201, priority: "Medium", duration: 7,  linkedTo: [27] },
  { id: 27, name: "Signing & Closing",                           defaultOffsetDays: 210, priority: "High",   duration: 1,  linkedTo: [] },
];

export const PHASES: Phase[] = [
  { id: 1, name: "Engagement & Pre-Market Prep", expectedDuration: "8–12 weeks" },
  { id: 2, name: "Marketing Materials (CIM, Teaser)", expectedDuration: "4–6 weeks" },
  { id: 3, name: "Go-to-Market Campaign", expectedDuration: "6 weeks" },
  { id: 4, name: "Management Presentations & LOIs", expectedDuration: "4–6 weeks" },
  { id: 5, name: "Confirmatory Diligence & Closing", expectedDuration: "8–12 weeks" },
];

// ── Deal-type-specific milestone templates ────────────────────────
export type SimpleMilestone = { name: string; offsetDays: number; priority: "High" | "Medium" | "Low"; duration: number };

export const CAPITAL_RAISE_MILESTONES: SimpleMilestone[] = [
  { name: "Engagement Letter Signed", offsetDays: 0, priority: "High", duration: 1 },
  { name: "Kickoff Meeting & Strategy Session", offsetDays: 3, priority: "High", duration: 1 },
  { name: "Financial Model & Projections Review", offsetDays: 5, priority: "High", duration: 14 },
  { name: "Data Room Setup & Document Collection", offsetDays: 7, priority: "Medium", duration: 14 },
  { name: "Draft Offering Memorandum / Pitch Deck", offsetDays: 14, priority: "High", duration: 21 },
  { name: "Offering Materials Approved by Client", offsetDays: 40, priority: "High", duration: 3 },
  { name: "Investor Target List Development", offsetDays: 14, priority: "Medium", duration: 14 },
  { name: "Investor Outreach Campaign Launch", offsetDays: 45, priority: "High", duration: 1 },
  { name: "Investor Meetings & Presentations", offsetDays: 50, priority: "High", duration: 30 },
  { name: "Investor Q&A & Follow-ups", offsetDays: 60, priority: "Medium", duration: 21 },
  { name: "Term Sheets / Indications Received", offsetDays: 85, priority: "High", duration: 1 },
  { name: "Term Sheet Negotiation & Selection", offsetDays: 87, priority: "High", duration: 10 },
  { name: "Due Diligence (Selected Investor)", offsetDays: 100, priority: "High", duration: 30 },
  { name: "Legal Documentation & Negotiation", offsetDays: 110, priority: "Medium", duration: 21 },
  { name: "Final Approvals & Board Consent", offsetDays: 130, priority: "High", duration: 5 },
  { name: "Closing & Funding", offsetDays: 140, priority: "High", duration: 1 },
];

export const DEBT_PLACEMENT_MILESTONES: SimpleMilestone[] = [
  { name: "Engagement Letter Signed", offsetDays: 0, priority: "High", duration: 1 },
  { name: "Kickoff & Credit Assessment", offsetDays: 3, priority: "High", duration: 1 },
  { name: "Financial Statements & Projections Compiled", offsetDays: 5, priority: "High", duration: 10 },
  { name: "Credit Package / Information Memo Drafted", offsetDays: 10, priority: "High", duration: 14 },
  { name: "Data Room Setup", offsetDays: 14, priority: "Medium", duration: 7 },
  { name: "Credit Package Approved by Client", offsetDays: 28, priority: "High", duration: 3 },
  { name: "Lender Target List Development", offsetDays: 14, priority: "Medium", duration: 10 },
  { name: "Lender Outreach & Distribution", offsetDays: 32, priority: "High", duration: 1 },
  { name: "Lender Meetings & Calls", offsetDays: 35, priority: "High", duration: 21 },
  { name: "Term Sheets Received", offsetDays: 60, priority: "High", duration: 1 },
  { name: "Term Sheet Comparison & Negotiation", offsetDays: 62, priority: "High", duration: 7 },
  { name: "Lender Selected / Commitment Letter", offsetDays: 70, priority: "High", duration: 1 },
  { name: "Lender Due Diligence", offsetDays: 72, priority: "Medium", duration: 21 },
  { name: "Credit Documentation & Legal Review", offsetDays: 80, priority: "Medium", duration: 21 },
  { name: "Final Credit Approval", offsetDays: 100, priority: "High", duration: 3 },
  { name: "Closing & Funding", offsetDays: 105, priority: "High", duration: 1 },
];

export const RESTRUCTURING_MILESTONES: SimpleMilestone[] = [
  { name: "Engagement & Scope Definition", offsetDays: 0, priority: "High", duration: 3 },
  { name: "Assess Current Financial Position", offsetDays: 3, priority: "High", duration: 14 },
  { name: "Stakeholder Mapping & Lender Communication", offsetDays: 5, priority: "High", duration: 7 },
  { name: "13-Week Cash Flow Model", offsetDays: 7, priority: "High", duration: 10 },
  { name: "Identify Restructuring Alternatives", offsetDays: 14, priority: "High", duration: 7 },
  { name: "Develop Restructuring Plan", offsetDays: 21, priority: "High", duration: 14 },
  { name: "Client & Board Approval of Plan", offsetDays: 38, priority: "High", duration: 3 },
  { name: "Negotiate with Existing Creditors", offsetDays: 42, priority: "High", duration: 30 },
  { name: "New Capital Source Identification (if needed)", offsetDays: 45, priority: "Medium", duration: 21 },
  { name: "Forbearance / Standstill Agreement", offsetDays: 50, priority: "High", duration: 7 },
  { name: "Term Sheet / Restructuring Agreement", offsetDays: 75, priority: "High", duration: 7 },
  { name: "Legal Documentation", offsetDays: 82, priority: "Medium", duration: 21 },
  { name: "Regulatory & Court Approvals (if applicable)", offsetDays: 90, priority: "Medium", duration: 14 },
  { name: "Implementation & Closing", offsetDays: 110, priority: "High", duration: 5 },
];

export const ESOP_MILESTONES: SimpleMilestone[] = [
  { name: "Engagement Letter Signed", offsetDays: 0, priority: "High", duration: 1 },
  { name: "Kickoff Meeting & ESOP Education", offsetDays: 3, priority: "High", duration: 1 },
  { name: "Feasibility Study & Repayment Analysis", offsetDays: 5, priority: "High", duration: 21 },
  { name: "Business Valuation (Independent Appraiser)", offsetDays: 10, priority: "High", duration: 30 },
  { name: "ESOP Transaction Structure Design", offsetDays: 28, priority: "High", duration: 14 },
  { name: "Draft ESOP Plan Document", offsetDays: 35, priority: "Medium", duration: 14 },
  { name: "Trustee Selection & Engagement", offsetDays: 35, priority: "High", duration: 7 },
  { name: "Financing Arrangement (Lender Outreach)", offsetDays: 42, priority: "High", duration: 21 },
  { name: "Term Sheet from Lender", offsetDays: 65, priority: "High", duration: 1 },
  { name: "Trustee Valuation Review & Fairness Opinion", offsetDays: 50, priority: "High", duration: 21 },
  { name: "Purchase Agreement Negotiation", offsetDays: 70, priority: "High", duration: 14 },
  { name: "Board & Shareholder Approval", offsetDays: 85, priority: "High", duration: 3 },
  { name: "ERISA & DOL Compliance Review", offsetDays: 75, priority: "Medium", duration: 14 },
  { name: "Final Legal Documentation", offsetDays: 90, priority: "Medium", duration: 14 },
  { name: "Closing & ESOP Effective Date", offsetDays: 105, priority: "High", duration: 1 },
];

export const BUYSIDE_ADVISORY_MILESTONES: SimpleMilestone[] = [
  { name: "Engagement Letter Signed", offsetDays: 0, priority: "High", duration: 1 },
  { name: "Kickoff & Acquisition Criteria Workshop", offsetDays: 3, priority: "High", duration: 1 },
  { name: "Define Target Profile & Screening Criteria", offsetDays: 5, priority: "High", duration: 7 },
  { name: "Target Universe Identification", offsetDays: 10, priority: "High", duration: 14 },
  { name: "Target Screening & Prioritization", offsetDays: 25, priority: "Medium", duration: 7 },
  { name: "Outreach Campaign to Priority Targets", offsetDays: 32, priority: "High", duration: 21 },
  { name: "Initial Target Discussions & NDAs", offsetDays: 40, priority: "Medium", duration: 21 },
  { name: "Preliminary Information Review", offsetDays: 50, priority: "Medium", duration: 14 },
  { name: "Management Meetings with Targets", offsetDays: 65, priority: "High", duration: 21 },
  { name: "Valuation Analysis & Offer Preparation", offsetDays: 75, priority: "High", duration: 10 },
  { name: "Letter of Intent (LOI) Submitted", offsetDays: 90, priority: "High", duration: 1 },
  { name: "LOI Negotiation & Execution", offsetDays: 92, priority: "High", duration: 10 },
  { name: "Confirmatory Due Diligence", offsetDays: 105, priority: "High", duration: 30 },
  { name: "Purchase Agreement Negotiation", offsetDays: 120, priority: "High", duration: 21 },
  { name: "Financing Arranged (if applicable)", offsetDays: 125, priority: "Medium", duration: 14 },
  { name: "Closing", offsetDays: 150, priority: "High", duration: 1 },
];

export const DEAL_TYPE_TEMPLATES: Record<string, SimpleMilestone[]> = {
  "Capital Raise": CAPITAL_RAISE_MILESTONES,
  "Debt Placement": DEBT_PLACEMENT_MILESTONES,
  "Restructuring": RESTRUCTURING_MILESTONES,
  "ESOP": ESOP_MILESTONES,
  "Buy-Side Advisory": BUYSIDE_ADVISORY_MILESTONES,
};

// Questions asked during project creation to adjust milestones
export interface WizardQuestion {
  id: string;
  question: string;
  dealTypes: string[]; // which deal types this applies to
}

export const WIZARD_QUESTIONS: WizardQuestion[] = [
  // M&A
  { id: "mva_done", question: "Has a Market Value Analysis already been completed?", dealTypes: ["M&A"] },
  { id: "qoe_engaged", question: "Has a Sell-Side QoE provider already been engaged?", dealTypes: ["M&A"] },
  { id: "cim_drafted", question: "Has the CIM / offering memorandum been started?", dealTypes: ["M&A", "Capital Raise"] },
  // Shared
  { id: "data_room_setup", question: "Has the data room already been set up?", dealTypes: ["M&A", "Capital Raise", "Debt Placement"] },
  { id: "teaser_drafted", question: "Has a teaser / blind profile already been drafted?", dealTypes: ["M&A", "Capital Raise"] },
  { id: "buyer_list_ready", question: "Has the buyer / investor / lender list been developed?", dealTypes: ["M&A", "Capital Raise", "Debt Placement", "Buy-Side Advisory"] },
  // Capital Raise
  { id: "financials_ready", question: "Are the financial model and projections complete?", dealTypes: ["Capital Raise", "Debt Placement", "ESOP"] },
  { id: "pitch_deck_ready", question: "Has a pitch deck or offering memo already been drafted?", dealTypes: ["Capital Raise"] },
  // Debt Placement
  { id: "credit_package_ready", question: "Has a credit package / information memo been prepared?", dealTypes: ["Debt Placement"] },
  // ESOP
  { id: "valuation_done", question: "Has an independent business valuation been completed?", dealTypes: ["ESOP"] },
  { id: "trustee_selected", question: "Has an ESOP trustee been selected?", dealTypes: ["ESOP"] },
  // Buy-Side
  { id: "criteria_defined", question: "Have the acquisition criteria and target profile been defined?", dealTypes: ["Buy-Side Advisory"] },
  { id: "targets_identified", question: "Have potential acquisition targets already been identified?", dealTypes: ["Buy-Side Advisory"] },
  // Restructuring
  { id: "cash_flow_model", question: "Has a 13-week cash flow model been prepared?", dealTypes: ["Restructuring"] },
  { id: "lender_communication", question: "Has communication with existing lenders begun?", dealTypes: ["Restructuring"] },
];

export const US_HOLIDAYS: HolidayDef[] = [
  { name: "Memorial Day", month: 4, dayOfWeek: 1, occurrence: "last" },
  { name: "Independence Day", month: 6, day: 4 },
  { name: "Labor Day", month: 8, dayOfWeek: 1, occurrence: 1 },
  { name: "Columbus Day", month: 9, dayOfWeek: 1, occurrence: 2 },
  { name: "Veterans Day", month: 10, day: 11 },
  { name: "Thanksgiving", month: 10, dayOfWeek: 4, occurrence: 4 },
  { name: "Christmas", month: 11, day: 25 },
];

export const TEAM_MEMBERS = ["TEAM"];

export function getHolidaysForYear(year: number): { date: Date; name: string }[] {
  const holidays: { date: Date; name: string }[] = [];

  US_HOLIDAYS.forEach((hol) => {
    if (hol.day !== undefined) {
      holidays.push({ date: new Date(year, hol.month, hol.day), name: hol.name });
    } else if (hol.dayOfWeek !== undefined) {
      let d: Date;
      if (hol.occurrence === "last") {
        d = new Date(year, hol.month + 1, 0);
        while (d.getDay() !== hol.dayOfWeek) {
          d.setDate(d.getDate() - 1);
        }
      } else {
        d = new Date(year, hol.month, 1);
        let count = 0;
        while (d.getMonth() === hol.month) {
          if (d.getDay() === hol.dayOfWeek) {
            count++;
            if (count === hol.occurrence) break;
          }
          d.setDate(d.getDate() + 1);
        }
      }
      holidays.push({ date: d, name: hol.name });
    }
  });

  return holidays;
}
